/**
 * Mobile controls -> abstract intents. A first-class touch scheme:
 *  - On-screen joystick (nipplejs) on the left half for movement.
 *  - Large GROW button (rapid-tap action) and a SHRINK button on the right.
 * Produces the identical InputFrame shape as desktop, so the game core is
 * unaware of the device.
 */
import nipplejs from 'nipplejs';
import type { JoystickManager } from 'nipplejs';
import { clamp } from '../core/math/vec2';
import { emptyFrame, type InputFrame, type InputSource } from './inputSource';

export interface MobileInputElements {
  joystickZone: HTMLElement;
  growButton: HTMLElement;
  shrinkButton: HTMLElement;
}

export class MobileInput implements InputSource {
  private readonly els: MobileInputElements;
  private manager: JoystickManager | null = null;

  private moveX = 0;
  private moveY = 0;
  private growClicks = 0;
  private shrinkClicks = 0;

  constructor(els: MobileInputElements) {
    this.els = els;
  }

  start(): void {
    this.manager = nipplejs.create({
      zone: this.els.joystickZone,
      mode: 'dynamic',
      color: 'rgba(255,255,255,0.5)',
      size: 110,
      restJoystick: true,
      fadeTime: 100,
    });
    this.manager.on('move', (_evt, data) => {
      if (!data?.vector) return;
      const throttle = clamp(data.force ?? 1, 0, 1);
      // nipplejs y points up; screen/world y points down.
      this.moveX = data.vector.x * throttle;
      this.moveY = -data.vector.y * throttle;
    });
    this.manager.on('end', () => {
      this.moveX = 0;
      this.moveY = 0;
    });

    this.els.growButton.addEventListener('pointerdown', this.onGrow);
    this.els.shrinkButton.addEventListener('pointerdown', this.onShrink);
  }

  stop(): void {
    this.manager?.destroy();
    this.manager = null;
    this.els.growButton.removeEventListener('pointerdown', this.onGrow);
    this.els.shrinkButton.removeEventListener('pointerdown', this.onShrink);
  }

  drain(): InputFrame {
    const frame: InputFrame = emptyFrame();
    frame.moveDir = { x: this.moveX, y: this.moveY };
    if (this.growClicks) frame.pulses.grow = this.growClicks;
    if (this.shrinkClicks) frame.pulses.shrink = this.shrinkClicks;
    this.growClicks = 0;
    this.shrinkClicks = 0;
    return frame;
  }

  private onGrow = (e: Event): void => {
    e.preventDefault();
    this.growClicks++;
  };

  private onShrink = (e: Event): void => {
    e.preventDefault();
    this.shrinkClicks++;
  };
}
